import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ApiClient from '../utils/ApiClient';
import StockAwareAds from '../components/AmazonAds/StockAwareAds';
import KeywordCampaign from '../components/AmazonAds/KeywordCampaign';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Amazon Ads — Sponsored Products campaign performance (US profile).
// Data comes from amazon_ads_sp_campaign_daily, filled by the daily 07:00 ET job or the
// "Sync from Amazon" button. Sales/orders are 14-day attributed, so the most recent ~2 weeks
// keep rising as late conversions land.

const RANGES = [7, 14, 30, 60, 90];

const num = (v) => (v === null || v === undefined ? 0 : Number(v));
const money = (v) => `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v) => num(v).toLocaleString();
const pct = (v) => (v === null || !isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`);
const ratio = (v) => (v === null || !isFinite(v) ? '—' : v.toFixed(2));

const derive = (r) => {
    const cost = num(r.cost), sales = num(r.sales), clicks = num(r.clicks), impressions = num(r.impressions);
    return {
        ...r,
        cost, sales, clicks, impressions,
        orders: num(r.orders),
        acos: sales > 0 ? cost / sales : (cost > 0 ? Infinity : null),
        roas: cost > 0 ? sales / cost : null,
        ctr: impressions > 0 ? clicks / impressions : null,
        cpc: clicks > 0 ? cost / clicks : null,
    };
};

const COLUMNS = [
    { key: 'campaign_name', label: 'Campaign', align: 'left' },
    { key: 'campaign_status', label: 'Status', align: 'left' },
    { key: 'budget_amount', label: 'Budget/day', fmt: money },
    { key: 'impressions', label: 'Impr.', fmt: int },
    { key: 'clicks', label: 'Clicks', fmt: int },
    { key: 'ctr', label: 'CTR', fmt: pct },
    { key: 'cost', label: 'Spend', fmt: money },
    { key: 'cpc', label: 'CPC', fmt: (v) => (v === null ? '—' : money(v)) },
    { key: 'orders', label: 'Orders', fmt: int },
    { key: 'sales', label: 'Sales', fmt: money },
    { key: 'acos', label: 'ACOS', fmt: (v) => (v === Infinity ? 'no sales' : pct(v)) },
    { key: 'roas', label: 'ROAS', fmt: ratio },
];

function Tile({ label, value, sub }) {
    return (
        <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
            {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

// fetched_at is stored in UTC without a zone marker; budget timestamps arrive as ISO with Z.
const localTime = (v, utcNaive) => {
    if (!v) return null;
    const d = new Date(utcNaive ? `${String(v).replace(' ', 'T')}Z` : v);
    return isNaN(d) ? null : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

function TodayPanel({ today }) {
    if (!today) return null;
    const t = derive(today.totals || {});
    const asOf = localTime(today.totals?.fetched_at_utc, true);
    return (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Today so far · {today.date} (Pacific)</h2>
                <span className="text-xs text-gray-500">
                    {asOf ? `Pulled from Amazon at ${asOf}` : 'Not pulled yet today'} · refreshes every 2 hours ·
                    Amazon's same-day data lags a few hours
                </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Tile label="Spend" value={money(t.cost)} sub={t.cpc === null ? null : `CPC ${money(t.cpc)}`} />
                <Tile label="Clicks" value={int(t.clicks)} sub={`${int(t.impressions)} impr.`} />
                <Tile label="Orders" value={int(t.orders)} />
                <Tile label="Sales" value={money(t.sales)} />
                <Tile label="ACOS" value={t.acos === Infinity ? 'no sales' : pct(t.acos)} />
                {(today.budget || []).length > 0 ? today.budget.map((b) => (
                    <Tile
                        key={b.campaignId}
                        label={`Budget used${today.budget.length > 1 ? ` · ${b.campaignName || b.campaignId}` : ''}`}
                        value={`${num(b.usagePercent).toFixed(1)}%`}
                        sub={`${money((num(b.budget) * num(b.usagePercent)) / 100)} of ${money(b.budget)}${localTime(b.updatedAt) ? ` · Amazon ${localTime(b.updatedAt)}` : ''}`}
                    />
                )) : <Tile label="Budget used" value="—" sub={today.budgetError ? 'unavailable' : null} />}
            </div>
        </div>
    );
}

function AmazonAds() {
    const [tab, setTab] = useState('performance');
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [today, setToday] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [sort, setSort] = useState({ key: 'cost', dir: 'desc' });
    const [statusFilter, setStatusFilter] = useState('ALL');

    const load = useCallback(async () => {
        setError(null);
        try {
            // Ads days are Pacific; the chart covers complete days only (ending yesterday, Pacific),
            // today's partial day is the Today panel.
            const pacificToday = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
            const end = new Date(`${pacificToday}T12:00:00Z`);
            end.setUTCDate(end.getUTCDate() - 1);
            const start = new Date(end.getTime() - (days - 1) * 86400000);
            const iso = (d) => d.toISOString().slice(0, 10);
            const [res, todayRes] = await Promise.all([
                ApiClient.get(`/api/amazon-ads/sp-campaigns?startDate=${iso(start)}&endDate=${iso(end)}`),
                ApiClient.get('/api/amazon-ads/today').catch(() => null),
            ]);
            setData(res.data);
            setToday(todayRes ? todayRes.data : null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load Amazon Ads data.');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    // While a sync runs on the server, re-check every 20s so the page fills in when it lands.
    useEffect(() => {
        if (!data?.syncRunning) return undefined;
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, [data?.syncRunning, load]);

    const sync = async () => {
        setMessage(null);
        try {
            const res = await ApiClient.post('/api/amazon-ads/sp-campaigns/sync');
            setMessage(res.data.message);
            setData((d) => (d ? { ...d, syncRunning: true } : d));
        } catch (err) {
            setMessage(err.response?.data?.error || 'Sync request failed.');
        }
    };

    const campaigns = useMemo(() => (data?.campaigns || []).map(derive), [data]);
    const statuses = useMemo(() => ['ALL', ...new Set(campaigns.map((c) => c.campaign_status).filter(Boolean))], [campaigns]);

    const rows = useMemo(() => {
        const filtered = statusFilter === 'ALL' ? campaigns : campaigns.filter((c) => c.campaign_status === statusFilter);
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const av = a[sort.key], bv = b[sort.key];
            if (typeof av === 'string' || typeof bv === 'string') return dir * String(av || '').localeCompare(String(bv || ''));
            const an = av === null || av === undefined ? -1 : Number(av);
            const bn = bv === null || bv === undefined ? -1 : Number(bv);
            return dir * (an - bn);
        });
    }, [campaigns, sort, statusFilter]);

    const totals = useMemo(() => derive(campaigns.reduce((t, c) => ({
        cost: t.cost + c.cost, sales: t.sales + c.sales, clicks: t.clicks + c.clicks,
        impressions: t.impressions + c.impressions, orders: t.orders + c.orders,
    }), { cost: 0, sales: 0, clicks: 0, impressions: 0, orders: 0 })), [campaigns]);

    const daily = data?.daily || [];
    const chartData = {
        labels: daily.map((d) => String(d.report_date).slice(5)),
        datasets: [
            { label: 'Spend (left axis)', data: daily.map((d) => num(d.cost)), borderColor: '#dc2626', backgroundColor: '#dc2626', tension: 0.2, pointRadius: 2, yAxisID: 'ySpend' },
            { label: 'Sales, 14d attr. (right axis)', data: daily.map((d) => num(d.sales)), borderColor: '#2563eb', backgroundColor: '#2563eb', tension: 0.2, pointRadius: 2, yAxisID: 'ySales' },
        ],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } } },
        // Spend is tiny next to sales (~$2/day vs ~$600/day), so each gets its own axis — on a
        // shared axis the spend line sits flat on zero and can't be read against sales.
        scales: {
            ySpend: {
                type: 'linear', position: 'left', beginAtZero: true,
                title: { display: true, text: 'Spend', color: '#dc2626' },
                ticks: { color: '#dc2626', callback: (v) => `$${v.toLocaleString()}` },
            },
            ySales: {
                type: 'linear', position: 'right', beginAtZero: true,
                title: { display: true, text: 'Sales', color: '#2563eb' },
                ticks: { color: '#2563eb', callback: (v) => `$${v.toLocaleString()}` },
                grid: { drawOnChartArea: false },
            },
        },
    };

    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 max-w-7xl mx-auto">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Amazon Ads — Sponsored Products</h1>
                        <p className="text-sm text-gray-600">
                            US · Occam's Razor{tab === 'performance' && data && ` · ${data.startDate} to ${data.endDate}. Sales/orders are 14-day attributed; the last two weeks are still settling.`}
                        </p>
                    </div>
                    {tab === 'performance' && <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-md shadow-sm">
                            {RANGES.map((r, i) => (
                                <button
                                    key={r}
                                    onClick={() => setDays(r)}
                                    className={`px-3 py-1.5 text-sm border border-gray-300 ${i === 0 ? 'rounded-l-md' : ''} ${i === RANGES.length - 1 ? 'rounded-r-md' : ''} ${i > 0 ? '-ml-px' : ''} ${days === r ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {r}d
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={sync}
                            disabled={data?.syncRunning}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            title="Pull the last 14 days plus today from Amazon now (jobs: 07:00 ET for 14 days, every 2 hours for today)"
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${data?.syncRunning ? 'animate-spin' : ''}`} />
                            {data?.syncRunning ? 'Syncing…' : 'Sync from Amazon'}
                        </button>
                    </div>}
                </div>

                <div className="border-b border-gray-200 mb-6 flex gap-6">
                    {[['performance', 'Performance'], ['stock', 'Stock-aware ads'], ['keywords', 'Keyword campaign']].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`pb-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === 'stock' ? <StockAwareAds /> : tab === 'keywords' ? <KeywordCampaign /> : (<>

                {message && <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800">{message}</div>}
                {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">{error}</div>}

                {!loading && <TodayPanel today={today} />}

                {loading ? (
                    <div className="text-gray-500">Loading…</div>
                ) : campaigns.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
                        No campaign data stored for this range yet. Use <span className="font-medium">Sync from Amazon</span> to pull the last 14 days and today.
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                            <Tile label="Spend" value={money(totals.cost)} />
                            <Tile label="Sales" value={money(totals.sales)} sub={`${int(totals.orders)} orders`} />
                            <Tile label="ACOS" value={pct(totals.acos)} />
                            <Tile label="ROAS" value={ratio(totals.roas)} />
                            <Tile label="Clicks" value={int(totals.clicks)} sub={`CTR ${pct(totals.ctr)}`} />
                            <Tile label="CPC" value={totals.cpc === null ? '—' : money(totals.cpc)} sub={`${int(totals.impressions)} impr.`} />
                        </div>

                        <div className="bg-white rounded-lg shadow p-4 mb-6">
                            <h2 className="text-sm font-semibold text-gray-700 mb-2">Daily spend vs. sales</h2>
                            <div className="h-64"><Line data={chartData} options={chartOptions} /></div>
                        </div>

                        <div className="bg-white rounded-lg shadow">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                <h2 className="text-sm font-semibold text-gray-700">Campaigns ({rows.length})</h2>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1"
                                >
                                    {statuses.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All statuses' : s}</option>)}
                                </select>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {COLUMNS.map((c) => (
                                                <th
                                                    key={c.key}
                                                    onClick={() => toggleSort(c.key)}
                                                    className={`px-3 py-2 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}
                                                >
                                                    {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {rows.map((r) => (
                                            <tr key={r.campaign_id} className="hover:bg-gray-50">
                                                {COLUMNS.map((c) => (
                                                    <td
                                                        key={c.key}
                                                        className={`px-3 py-2 tabular-nums ${c.align === 'left' ? 'text-left' : 'text-right'} ${c.key === 'campaign_name' ? 'max-w-xs truncate text-gray-900' : 'text-gray-700'}`}
                                                        title={c.key === 'campaign_name' ? r.campaign_name : undefined}
                                                    >
                                                        {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? '—')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
                </>)}
            </div>
        </div>
    );
}

export default AmazonAds;
